import { useEffect, useState, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { apiClient } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Video,
  CheckCircle2,
  XCircle,
  Mail,
  User as UserIcon,
  FileText,
  Loader2,
} from "lucide-react";

const STATUS_META = {
  requested: { label: "À traiter", color: "bg-amber-100 text-amber-900 border-amber-300" },
  confirmed: { label: "Confirmé", color: "bg-emerald-100 text-emerald-900 border-emerald-300" },
  declined: { label: "Refusé", color: "bg-rose-100 text-rose-900 border-rose-300" },
  cancelled: { label: "Annulé", color: "bg-slate-200 text-slate-700 border-slate-300" },
  completed: { label: "Terminé", color: "bg-emerald-50 text-emerald-900 border-emerald-200" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const FILTERS = [
  { key: "all", label: "Tous" },
  { key: "requested", label: "À traiter" },
  { key: "confirmed", label: "Confirmés" },
  { key: "completed", label: "Terminés" },
  { key: "declined", label: "Refusés" },
];

export default function ExpertRdvDashboard() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await apiClient.get("/expert/appointments", { params });
      setItems(data.appointments || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error("Impossible de charger les rendez-vous.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { refresh(); }, [refresh]);

  const accept = async (appt) => {
    setActingId(appt.id);
    try {
      await apiClient.post(`/expert/appointments/${appt.id}/accept`, {
        note_for_user: (noteDraft[appt.id] || "").trim() || null,
      });
      toast.success("Rendez-vous confirmé ✓");
      setNoteDraft((d) => ({ ...d, [appt.id]: "" }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirmation impossible.");
    } finally {
      setActingId(null);
    }
  };

  const decline = async (appt) => {
    setActingId(appt.id);
    try {
      await apiClient.post(`/expert/appointments/${appt.id}/decline`, {
        reason: (noteDraft[appt.id] || "").trim() || null,
      });
      toast.success("Demande refusée.");
      setNoteDraft((d) => ({ ...d, [appt.id]: "" }));
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refus impossible.");
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-8" data-testid="expert-rdv-header">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Espace Spécialiste</p>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900 mt-1">
            Rendez-vous & visioconférences
          </h1>
          <p className="text-slate-600 mt-2 text-base">
            Acceptez ou refusez vos demandes de consultation. Le client est notifié par email automatiquement.
          </p>
        </header>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="rdv-filters">
          {FILTERS.map((f) => {
            const count = f.key === "all"
              ? Object.values(counts).reduce((a, b) => a + b, 0)
              : counts[f.key] || 0;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                data-testid={`rdv-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-full text-sm border transition ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                }`}
              >
                {f.label}
                <span className={`ml-2 text-xs ${active ? "text-slate-300" : "text-slate-400"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500" data-testid="rdv-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center border-dashed" data-testid="rdv-empty">
            <Calendar className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <h3 className="text-lg font-medium text-slate-800">Aucun rendez-vous</h3>
            <p className="text-sm text-slate-500 mt-1">
              Les demandes de visioconférence apparaîtront ici dès qu'un client vous sollicitera.
            </p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="rdv-list">
            {items.map((appt) => {
              const meta = STATUS_META[appt.status] || STATUS_META.requested;
              const isRequested = appt.status === "requested";
              const isConfirmed = appt.status === "confirmed";
              const busy = actingId === appt.id;
              return (
                <Card
                  key={appt.id}
                  data-testid={`rdv-card-${appt.id}`}
                  className="p-5 sm:p-6 border border-slate-200 hover:border-slate-300 transition"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={`${meta.color} border`} data-testid={`rdv-status-${appt.id}`}>
                          {meta.label}
                        </Badge>
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {appt.duration_min} min
                        </span>
                        {appt.workspace_id && (
                          <span className="text-xs text-slate-500 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Dossier lié
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 leading-snug">
                        {appt.subject}
                      </h3>
                      <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {fmtDateTime(appt.requested_at)}
                        </div>
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-slate-400" />
                          {appt.contact_name || "—"}
                        </div>
                        {appt.contact_email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            {appt.contact_email}
                          </div>
                        )}
                        {appt.contact_phone && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <span className="text-slate-400">☎</span>
                            {appt.contact_phone}
                          </div>
                        )}
                      </div>
                      {appt.message && (
                        <p className="mt-3 p-3 bg-slate-50 rounded-lg text-sm text-slate-700 border border-slate-100 whitespace-pre-line">
                          {appt.message}
                        </p>
                      )}
                      {appt.note_for_user && (
                        <p className="mt-2 text-xs text-emerald-700">
                          <strong>Note envoyée au client :</strong> {appt.note_for_user}
                        </p>
                      )}
                    </div>

                    {isConfirmed && appt.meeting_url && (
                      <a
                        href={appt.meeting_url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`rdv-join-${appt.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 self-start"
                      >
                        <Video className="w-4 h-4" /> Rejoindre la visio
                      </a>
                    )}
                  </div>

                  {isRequested && (
                    <div className="mt-5 pt-5 border-t border-slate-100">
                      <Textarea
                        data-testid={`rdv-note-${appt.id}`}
                        placeholder="Message optionnel pour le client (visible dans l'email de confirmation/refus)"
                        value={noteDraft[appt.id] || ""}
                        onChange={(e) => setNoteDraft((d) => ({ ...d, [appt.id]: e.target.value }))}
                        rows={2}
                        className="text-sm"
                      />
                      <div className="flex gap-2 mt-3 justify-end">
                        <Button
                          variant="outline"
                          data-testid={`rdv-decline-btn-${appt.id}`}
                          disabled={busy}
                          onClick={() => decline(appt)}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                        <Button
                          data-testid={`rdv-accept-btn-${appt.id}`}
                          disabled={busy}
                          onClick={() => accept(appt)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          {busy ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-1" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                          )}
                          Accepter
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
